/**
 * Splash — refined first-run language picker.
 * Clean abstract mark instead of cartoon storefront.
 */

import { useEffect, useState } from 'react';
import { useV4, LANGS, Lang } from './i18n';

export default function SplashV4() {
  const { settings, setLang, t } = useV4();
  const [visible, setVisible] = useState(() => {
    try { return sessionStorage.getItem('client-pwa-v4-splash-shown') !== '1'; } catch { return true; }
  });

  useEffect(() => {
    if (!visible) return;
    if (settings.lang) {
      const timer = setTimeout(dismiss, 1500);
      return () => clearTimeout(timer);
    }
  }, [visible, settings.lang]);

  const dismiss = () => {
    try { sessionStorage.setItem('client-pwa-v4-splash-shown', '1'); } catch {}
    setVisible(false);
  };

  const choose = (l: Lang) => {
    setLang(l);
    setTimeout(dismiss, 200);
  };

  if (!visible) return null;

  const langDef = LANGS.find(l => l.code === (settings.lang || 'en'))!;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-between bg-card px-6 pb-10 pt-20 animate-in fade-in duration-500"
      dir={langDef.dir}
      style={{ fontFamily: langDef.font }}
    >
      <div className="flex flex-1 flex-col items-center justify-center text-center max-w-md">
        {/* Abstract mark — overlapping rounded squares in a refined indigo/teal gradient */}
        <div className="relative mb-10 h-20 w-20">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 rotate-6" />
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 -rotate-6 opacity-90" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-3xl font-bold text-white tracking-tight">R</span>
          </div>
        </div>

        <h1 className="text-3xl font-bold tracking-tight text-foreground mb-3">{t('app_name')}</h1>
        <p className="text-sm leading-relaxed text-muted-foreground mb-12 max-w-xs italic">Your AI accountant in your pocket.</p>

        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60 mb-4">
          {t('pick_language')}
        </p>
        <div className="grid grid-cols-2 gap-2 w-full">
          {LANGS.map(l => {
            const isActive = settings.lang === l.code;
            return (
              <button
                key={l.code}
                onClick={() => choose(l.code)}
                dir={l.dir}
                style={{ fontFamily: l.font }}
                className={`flex flex-col items-start gap-0.5 rounded-xl border px-4 py-3 text-start transition-all ${
                  isActive
                    ? 'border-[var(--v4-accent)] bg-[var(--v4-accent-tint)] shadow-sm'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                }`}
              >
                <span className="text-base font-semibold text-foreground">{l.native}</span>
                <span className="text-[11px] text-muted-foreground">{l.english}</span>
              </button>
            );
          })}
        </div>
      </div>

      {settings.lang && (
        <button
          onClick={dismiss}
          className="w-full max-w-md rounded-xl bg-[var(--v4-accent)] px-6 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-[var(--v4-accent-hover)] transition-colors"
        >
          {t('continue')} →
        </button>
      )}
    </div>
  );
}
